// apps/admin/src/web/env.ts
//
// Typed, validated deployed configuration for the admin SPA -- the sole owner
// of it (ADR-0178 D1). Before this file the values were read raw from
// `import.meta.env` at eight sites across two files, which was harmless only
// while every value was compiled into the bundle.
//
// Deliberately under `src/web/`: `src/server/env.ts` is the Node half and parses
// the Cloud Run runtime env at boot. This is the browser half, and the two
// surfaces share no values.
//
// The one sanctioned exception to this chokepoint is local-dev-only variables
// (`VITE_FIREBASE_AUTH_EMULATOR_HOST`, `VITE_ADMIN_DEV_SESSION`), which are
// never set in a container and stay on `import.meta.env` at their use site.

import { z } from 'zod';

import { readRuntimeConfig } from './runtime-config';

export const EnvSchema = z.object({
  /** CIP (GCP Identity Platform) web API key. A PUBLIC client identifier. */
  FIREBASE_API_KEY: z.string().default(''),
  FIREBASE_AUTH_DOMAIN: z.string().default(''),
  FIREBASE_PROJECT_ID: z.string().default(''),
  /**
   * Admin CIP tenant id (`admin-XXXXX`). The SPA sets `auth.tenantId` from it so
   * sign-in routes to the isolated admin tenant rather than the project default
   * pool. Empty leaves the default pool, which is why it is set explicitly in
   * every deployed environment.
   */
  CIP_TENANT_ID: z.string().default(''),
  /** Sentry DSN (public). Empty disables Sentry. */
  SENTRY_DSN: z.string().default(''),
  /** Deploy environment tag for Sentry (staging | prod | dev). */
  HEYROOT_ENV: z.string().default('dev'),
  /** Release suffix for Sentry. The tree hash in CI-built images. */
  BUILD_SHA: z.string().default('dev'),
});

function parseEnv(): z.infer<typeof EnvSchema> {
  // Runtime config (window.__HEYROOT_CONFIG__, served by the Hono /config.js
  // route) layered over build-time import.meta.env.
  const metaEnv = readRuntimeConfig();

  const raw: Record<string, string | undefined> = {
    FIREBASE_API_KEY: metaEnv['VITE_FIREBASE_API_KEY'],
    FIREBASE_AUTH_DOMAIN: metaEnv['VITE_FIREBASE_AUTH_DOMAIN'],
    FIREBASE_PROJECT_ID: metaEnv['VITE_FIREBASE_PROJECT_ID'],
    CIP_TENANT_ID: metaEnv['VITE_CIP_TENANT_ID'],
    SENTRY_DSN: metaEnv['VITE_SENTRY_DSN'],
    HEYROOT_ENV: metaEnv['VITE_HEYROOT_ENV'],
    BUILD_SHA: metaEnv['VITE_BUILD_SHA'],
  };

  const result = EnvSchema.safeParse(raw);
  if (result.success) return result.data;

  // Non-fatal, and field by field: every field declares a default, so dropping
  // the offending keys yields defaults for exactly those and real values for the
  // rest. Resetting the whole object would blank the CIP config over an
  // unrelated bad value.
  if (typeof console !== 'undefined') {
    console.warn('[env] admin env parse warning:', result.error.format());
  }
  const badKeys = new Set(result.error.issues.map((issue) => String(issue.path[0])));
  const pruned = Object.fromEntries(Object.entries(raw).filter(([key]) => !badKeys.has(key)));
  const retry = EnvSchema.safeParse(pruned);
  return retry.success ? retry.data : EnvSchema.parse({});
}

export const env = parseEnv();
